import React, { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, MessageCircle } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { waLink, quoteMessage } from "@/lib/whatsapp";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/products", label: "Products", children: [
    { to: "/products/grp-sectional", label: "GRP Sectional" },
    { to: "/products/hdg-steel", label: "HDG Steel" },
    { to: "/products/gls-gfs", label: "GLS / GFS" },
    { to: "/products/zinc-aluminium", label: "Zinc Aluminium" },
    { to: "/products/grp-cylindrical", label: "GRP Cylindrical" },
  ]},
  { to: "/industries", label: "Industries" },
  { to: "/projects", label: "Projects" },
  { to: "/global-presence", label: "Global" },
  { to: "/quality", label: "Quality" },
  { to: "/resources", label: "Resources", children: [
    { to: "/resources", label: "Company Profile" },
    { to: "/resources", label: "Catalogues" },
    { to: "/resources", label: "Technical Documents" },
    { to: "/resources", label: "Certifications" },
    { to: "/resources", label: "Downloads" },
  ]},
  { to: "/news", label: "News" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [desktopExpanded, setDesktopExpanded] = useState(null);
  const { settings } = useSettings();
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => { setOpen(false); }, [location.pathname]);

  const handleLogoClick = (e) => {
    // On home: force scroll to top. Otherwise let router navigate to /
    if (location.pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      // navigate handled by <Link>, then PublicShell effect scrolls to top
    }
  };

  return (
    <header data-testid="site-header" className="sticky top-0 z-40 border-b border-black/5 bg-white/85 backdrop-blur-md">
      <div className="container-x px-5 md:px-8 flex items-center justify-between h-[64px] md:h-[74px]">
        <Link to="/" onClick={handleLogoClick} data-testid="brand-link" className="flex items-center gap-2 md:gap-3 shrink-0">
          <img src="/pipeco-logo.png" alt="PIPECO GROUP" className="h-9 md:h-11 w-auto" data-testid="brand-logo"/>
        </Link>

        <nav className="hidden xl:flex items-center gap-7">
          {NAV.map((n) => (
            <div key={n.to} className="relative group" onMouseEnter={()=>n.children && setDesktopExpanded(n.label)} onMouseLeave={()=>setDesktopExpanded(null)}>
              <NavLink to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g,'-')}`}
                className={({isActive}) => `text-[13px] font-semibold tracking-wide uppercase text-slate-800 hover-line inline-flex items-center gap-1 ${isActive?"text-pipeco-green":""}`}>
                {n.label}
                {n.children && <ChevronDown size={13} className="opacity-70"/>}
              </NavLink>
              {n.children && (
                <div className={`absolute left-0 top-full pt-3 transition-opacity ${desktopExpanded===n.label ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}>
                  <div className="bg-white border border-black/10 shadow-xl min-w-[240px] py-2">
                    {n.children.map((c) => (
                      <Link key={c.to+c.label} to={c.to} className="block px-4 py-2 text-[13px] text-slate-700 hover:bg-pipeco-green hover:text-white transition-colors">{c.label}</Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-3">
          <a data-testid="header-get-quote" href={waLink(settings.whatsapp_number, quoteMessage())} target="_blank" rel="noreferrer" className="btn-primary text-[11px] px-4 py-2.5 whitespace-nowrap">
            <MessageCircle size={15}/> Get a Quote
          </a>
        </div>

        <button data-testid="mobile-menu-toggle" onClick={()=>setOpen(!open)} className="xl:hidden p-2" aria-label="Menu">
          {open ? <X size={26}/> : <Menu size={26}/>}
        </button>
      </div>

      {open && (
        <div className="xl:hidden border-t border-black/10 bg-white">
          <div className="px-5 py-4">
            {NAV.map((n) => (
              <div key={n.to+"m"} className="border-b border-black/5">
                <div className="flex items-center justify-between">
                  <Link to={n.to} className="block py-3 text-[13px] font-semibold uppercase tracking-wide text-slate-800">{n.label}</Link>
                  {n.children && <button onClick={()=>setExpanded(expanded===n.label?null:n.label)} className="p-2"><ChevronDown size={16} className={`transition-transform ${expanded===n.label?"rotate-180":""}`}/></button>}
                </div>
                {n.children && expanded===n.label && (
                  <div className="pb-3 pl-3">
                    {n.children.map((c) => <Link key={c.to+c.label+"m"} to={c.to} className="block py-2 text-[12.5px] text-slate-600">{c.label}</Link>)}
                  </div>
                )}
              </div>
            ))}
            <a href={waLink(settings.whatsapp_number, quoteMessage())} target="_blank" rel="noreferrer" className="btn-primary w-full justify-center mt-4">
              <MessageCircle size={15}/> Get a Quote
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
